import React from 'react';
import Box from '@material-ui/core/Box';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Drawer from '@material-ui/core/Drawer';
import Accordion from '@material-ui/core/Accordion';
import AccordionSummary from '@material-ui/core/AccordionSummary';
import AccordionDetails from '@material-ui/core/AccordionDetails';
import Button from '@material-ui/core/Button';
import Checkbox from '@material-ui/core/Checkbox';
import Chip from '@material-ui/core/Chip';
import CircularProgress from '@material-ui/core/CircularProgress';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import FormControl from '@material-ui/core/FormControl';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormLabel from '@material-ui/core/FormLabel';
import Grid from '@material-ui/core/Grid';
import IconButton from '@material-ui/core/IconButton';
import MenuItem from '@material-ui/core/MenuItem';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import Tooltip from '@material-ui/core/Tooltip';
import Typography from '@material-ui/core/Typography';
import TextField from '@material-ui/core/TextField';
import CloseIcon from '@material-ui/icons/Close';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import RefreshIcon from '@material-ui/icons/Refresh';
import { makeStyles } from '@material-ui/core/styles';
import { useSelector } from 'react-redux';
import { QRCodeSVG } from 'qrcode.react';
import ExerciseLibrary from './exerciseLibrary';
import ExerciseImage, { resolveExerciseImageUrl } from './ExerciseImage';

const backendBaseUrl = process.env.REACT_APP_BACKEND_URL || 'https://api.virtus-one.com';

const useStyles = makeStyles((theme) => ({
  orangeFocused: {
    '& .MuiOutlinedInput-root': {
      borderRadius: 14,
      backgroundColor: '#ffffff',
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderRadius: 14,
      borderColor: '#dddddd',
    },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: '#FF7F27',
    },
    '& .MuiInputLabel-outlined.Mui-focused': {
      color: '#FF7F27',
    },
    '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: '#FF7F27',
    },
    '& .MuiInputBase-input': {
      fontSize: 'clamp(0.72rem, calc(2px + 0.722vw), 1.0rem)',
    },
    '& .MuiInputLabel-root': {
      fontSize: 'clamp(0.65rem, calc(1px + 0.72vw), 0.9rem)',
      color: '#666666',
    },
  },
  dialogTitle: {
    borderBottom: '1px solid #eeeeee',
    padding: '16px 24px',
  },
  dialogTitleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  },
  dialogTitleActions: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flexShrink: 0,
  },
  dialogTitleText: {
    flex: 1,
    color: '#002D55',
    fontWeight: 600,
    fontSize: 'clamp(0.95rem, calc(4px + 0.72vw), 1.15rem)',
  },
  dialogCounter: {
    fontSize: 'clamp(0.62rem, 0.7vw, 0.81rem)',
    fontWeight: 700,
    color: '#002D55',
    backgroundColor: '#f5f5f5',
    border: '1px solid #dddddd',
    borderRadius: 14,
    padding: '4px 12px',
    whiteSpace: 'nowrap',
    flexShrink: 0,
  },
  closeButton: {
    color: '#64748B',
    padding: 6,
  },
  dialogContent: {
    padding: '24px !important',
    overflow: 'auto',
    backgroundColor: '#e6eaf0',
  },
  sectionLabel: {
    fontSize: 12,
    color: '#666666',
    marginBottom: 8,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  adaptiveSectionLabel: {
    marginTop: 20,
  },
  sectionPanel: {
    backgroundColor: '#f7faff',
    borderRadius: 18,
    padding: 16,
    marginBottom: 24,
    border: '1px solid #dce8f5',
  },
  mediaDrawer: {
    backgroundColor: '#ffffff',
    borderRadius: 16,
    border: '1px solid #dce8f5',
    boxShadow: 'none',
    overflow: 'hidden',
    '&:before': {
      display: 'none',
    },
  },
  mediaDrawerSummary: {
    padding: '0 16px',
    minHeight: 0,
    '&.Mui-expanded': {
      minHeight: 0,
    },
    '& .MuiAccordionSummary-content': {
      margin: '12px 0',
    },
    '& .MuiAccordionSummary-content.Mui-expanded': {
      margin: '12px 0',
    },
  },
  mediaDrawerTitle: {
    color: '#002D55',
    fontWeight: 700,
    fontSize: 'clamp(0.76rem, 0.85vw, 0.92rem)',
  },
  mediaDrawerHint: {
    color: '#64748B',
    fontSize: 'clamp(0.64rem, 0.72vw, 0.78rem)',
    marginTop: 2,
    marginBottom: 16,
  },
  mediaDrawerInput: {
    '& .MuiOutlinedInput-root': {
      minHeight: 34,
    },
    '& .MuiInputBase-input': {
      fontSize: 'clamp(0.66rem, 0.74vw, 0.8rem)',
      lineHeight: 1.2,
      paddingTop: 8.5,
      paddingBottom: 8.5,
    },
    '& .MuiInputLabel-root': {
      fontSize: 'clamp(0.62rem, 0.7vw, 0.76rem)',
    },
    '& .MuiFormHelperText-root': {
      fontSize: 'clamp(0.6rem, 0.68vw, 0.74rem)',
      lineHeight: 1.35,
      marginLeft: 2,
      marginRight: 2,
    },
  },
  mediaDrawerDetails: {
    display: 'block',
    padding: '0 16px 16px',
  },
  adaptiveChoiceList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  adaptiveChoiceCard: {
    borderRadius: 14,
    border: '1px solid #dce8f5',
    backgroundColor: '#f7faff',
    padding: '10px 12px',
  },
  adaptiveChoiceHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 12,
    marginBottom: 8,
    flexWrap: 'wrap',
  },
  adaptiveChoiceHeaderActions: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flexWrap: 'wrap',
  },
  adaptiveChoiceTitle: {
    color: '#002D55',
    fontWeight: 700,
    fontSize: 'clamp(0.74rem, 0.82vw, 0.9rem)',
  },
  adaptiveChoiceError: {
    color: '#c62828',
    fontSize: 'clamp(0.68rem, 0.76vw, 0.82rem)',
    fontWeight: 600,
  },
  adaptiveChoiceActionsRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    flexWrap: 'nowrap',
    gap: 8,
    width: '100%',
    [theme.breakpoints.down('md')]: {
      flexWrap: 'wrap',
    },
  },
  adaptiveChoiceSelectionItem: {
    flex: '1 1 0',
    minWidth: 0,
  },
  adaptiveChoiceButtonGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flexShrink: 0,
    [theme.breakpoints.down('md')]: {
      flexWrap: 'wrap',
    },
  },
  adaptiveRememberOption: {
    margin: 0,
    whiteSpace: 'nowrap',
    flexShrink: 0,
    '& .MuiFormControlLabel-label': {
      color: '#002D55',
      fontSize: 'clamp(0.62rem, 0.7vw, 0.76rem)',
      fontWeight: 600,
      whiteSpace: 'nowrap',
    },
    '& .MuiCheckbox-root': {
      padding: 4,
      marginRight: 2,
    },
  },
  adaptiveStatusChip: {
    backgroundColor: '#edf5fc',
    color: '#002D55',
    fontWeight: 700,
    height: 24,
  },
  adaptiveChoiceSelectionBox: {
    borderRadius: 14,
    border: '1px solid #dce8f5',
    backgroundColor: '#ffffff',
    padding: '6px 10px',
    minHeight: 34,
    display: 'flex',
    alignItems: 'center',
    overflow: 'hidden',
  },
  adaptiveChoiceSelectionText: {
    color: '#002D55',
    fontWeight: 600,
    fontSize: 'clamp(0.66rem, 0.74vw, 0.8rem)',
    lineHeight: 1.2,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    width: '100%',
  },
  adaptiveChoiceSelectionEmpty: {
    color: '#64748B',
    fontSize: 'clamp(0.64rem, 0.72vw, 0.78rem)',
    lineHeight: 1.2,
  },
  adaptiveChoiceActionButton: {
    '&&': {
      minWidth: 112,
      height: 34,
      minHeight: 30,
      padding: '0 10px',
      fontSize: 'clamp(0.64rem, 0.72vw, 0.78rem)',
      lineHeight: 1,
    },
  },
  adaptiveChoiceHeaderButton: {
    width: 24,
    height: 24,
    padding: 0,
    borderRadius: '50%',
    border: '1px solid #dce8f5',
    backgroundColor: '#ffffff',
    color: '#002D55',
    '&:hover': {
      backgroundColor: '#edf5fc',
    },
    '&.Mui-disabled': {
      backgroundColor: '#f7faff',
      color: '#94A3B8',
      borderColor: '#dce8f5',
    },
  },
  adaptivePickerPaper: {
    width: 'calc(100vw - 24px)',
    maxWidth: 960,
    backgroundColor: '#f0f4fa',
    overflowX: 'hidden',
    overflowY: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    [theme.breakpoints.up('lg')]: {
      width: 'calc(100vw - 48px)',
      maxWidth: 1280,
    },
    [theme.breakpoints.up('xl')]: {
      width: 'calc(100vw - 64px)',
      maxWidth: 1640,
    },
  },
  adaptivePickerBody: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    overflow: 'hidden',
  },
  adaptivePickerHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 12,
    padding: '16px 18px 12px',
    borderBottom: '1px solid #dce8f5',
    backgroundColor: '#f0f4fa',
  },
  adaptivePickerTitle: {
    color: '#002D55',
    fontWeight: 700,
    fontSize: 'clamp(0.84rem, 0.92vw, 1rem)',
  },
  adaptivePickerSubtitle: {
    color: '#64748B',
    fontSize: 'clamp(0.68rem, 0.74vw, 0.82rem)',
    marginTop: 4,
  },
  mediaConsentPanel: {
    marginTop: 16,
    padding: '12px 14px',
    borderRadius: 14,
    backgroundColor: '#f7faff',
    border: '1px solid #dce8f5',
  },
  mediaConsentLabel: {
    color: '#002D55',
    fontWeight: 700,
    fontSize: 'clamp(0.72rem, 0.82vw, 0.88rem)',
    marginBottom: 8,
  },
  mediaConsentOption: {
    marginRight: 18,
    '& .MuiFormControlLabel-label': {
      color: '#002D55',
      fontSize: 'clamp(0.68rem, 0.78vw, 0.84rem)',
      fontWeight: 600,
    },
  },
  mediaConsentHint: {
    color: '#4b5f75',
    fontSize: 'clamp(0.66rem, 0.72vw, 0.8rem)',
    marginTop: 6,
  },
  mediaConsentError: {
    color: '#c62828',
    fontSize: 'clamp(0.66rem, 0.72vw, 0.8rem)',
    fontWeight: 600,
    marginTop: 6,
  },
  mediaPreview: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 'clamp(88px, 9vw, 140px)',
    backgroundColor: '#ffffff',
    borderRadius: 14,
    border: '1px solid #e6e6e6',
    overflow: 'hidden',
    padding: '16px 20px',
    gap: 6,
    boxSizing: 'border-box',
  },
  mediaPlaceholderTitle: {
    color: '#002D55',
    fontWeight: 600,
    fontSize: 'clamp(0.72rem, 0.8vw, 0.88rem)',
    textAlign: 'center',
  },
  mediaPlaceholderText: {
    color: '#666666',
    fontSize: 'clamp(0.62rem, 0.7vw, 0.76rem)',
    textAlign: 'center',
    maxWidth: 220,
    paddingTop: 6,
  },
  mediaPreviewImage: {
    maxWidth: '100%',
    maxHeight: 'clamp(88px, 9vw, 140px)',
    display: 'block',
    borderRadius: 12,
    objectFit: 'cover',
  },
  mediaPreviewVideo: {
    width: '100%',
    maxHeight: 'clamp(120px, 14vw, 220px)',
    display: 'block',
    borderRadius: 12,
    backgroundColor: '#000000',
  },
  mediaPreviewEmbed: {
    width: '100%',
    height: 'clamp(160px, 18vw, 280px)',
    border: 'none',
    borderRadius: 12,
    backgroundColor: '#000000',
  },
  phoneUploadQrCard: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 6,
    backgroundColor: '#ffffff',
    borderRadius: 14,
    padding: '28px 12px 10px',
    border: '1px solid #dce8f5',
    boxShadow: '0 10px 24px rgba(0, 45, 85, 0.08)',
  },
  phoneUploadSidePanel: {
    height: '100%',
    minHeight: 0,
    maxHeight: 210,
    display: 'flex',
    alignItems: 'stretch',
  },
  phoneUploadQrCardCompact: {
    width: '100%',
    height: '100%',
    minHeight: 0,
    maxHeight: 210,
    justifyContent: 'flex-start',
    padding: '10px',
    gap: 4,
    overflow: 'hidden',
  },
  phoneUploadQrPlaceholder: {
    width: '100%',
    height: '100%',
    minHeight: 0,
    maxHeight: 104,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    backgroundColor: '#ffffff',
    borderRadius: 14,
    border: '1px dashed #dce8f5',
    padding: '10px 8px',
    boxSizing: 'border-box',
    textAlign: 'center',
    overflow: 'hidden',
  },
  phoneUploadQrActionLeft: {
    position: 'absolute',
    top: 6,
    left: 6,
    width: 24,
    height: 24,
    backgroundColor: '#f7faff',
    border: '1px solid #dce8f5',
    color: '#002D55',
    '&:hover': {
      backgroundColor: '#edf5fc',
    },
  },
  phoneUploadQrActionRight: {
    position: 'absolute',
    top: 6,
    right: 6,
    width: 24,
    height: 24,
    backgroundColor: '#f7faff',
    border: '1px solid #dce8f5',
    color: '#002D55',
    '&:hover': {
      backgroundColor: '#edf5fc',
    },
  },
  phoneUploadInlineLink: {
    color: '#0b6aa2',
    fontWeight: 700,
    textDecoration: 'underline',
    cursor: 'pointer',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  phoneUploadHelper: {
    color: '#4b5f75',
    fontSize: 'clamp(0.7rem, 0.82vw, 0.84rem)',
    lineHeight: 1.55,
  },
  phoneUploadError: {
    color: '#c62828',
    fontSize: 'clamp(0.68rem, 0.8vw, 0.82rem)',
    fontWeight: 600,
  },
  secondaryButton: {
    borderRadius: 50,
    textTransform: 'none',
    fontWeight: 600,
    padding: 'clamp(4px, 0.45vw, 6px) clamp(10px, 1.1vw, 16px)',
  },
  confirmButton: {
    backgroundColor: '#002D55',
    color: '#FFFFFF',
    borderRadius: 50,
    border: 'none',
    boxShadow: 'none',
    padding: 'clamp(4px, 0.45vw, 6px) clamp(10px, 1.1vw, 16px)',
    fontSize: 'clamp(0.69rem, 0.75vw, 0.875rem)',
    fontWeight: 600,
    textTransform: 'none',
    minWidth: 'clamp(84px, 8vw, 100px)',
    '&:hover': {
      backgroundColor: '#FF7F27',
      boxShadow: 'none',
    },
  },
  cancelButton: {
    backgroundColor: '#e6eaf0',
    color: '#002D55',
    fontWeight: 'bold',
    borderRadius: 50,
    border: '2px solid transparent',
    boxShadow: 'none',
    padding: 'clamp(4px, 0.45vw, 6px) clamp(10px, 1.1vw, 16px)',
    fontSize: 'clamp(0.69rem, 0.75vw, 0.875rem)',
    textTransform: 'none',
    minWidth: 'clamp(84px, 8vw, 100px)',
    '&:hover': {
      backgroundColor: '#e6eaf0',
      color: '#FF7F27',
      border: '2px solid #FF7F27',
      boxShadow: 'none',
    },
  },
  dialogActions: {
    padding: 16,
    gap: 8,
    justifyContent: 'flex-end',
    borderTop: '1px solid #eeeeee',
    backgroundColor: '#ffffff',
  },
}));

const FrequencyType = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
];

const WEIGHT_DOSE_OPTIONS = {
  weight_kg: { field: 'weightKg', label: 'Weight (Kg)', tooltip: 'Kilograms per rep/set.' },
  weight_total_kg: { field: 'weightTotalKg', label: 'Weight (Total Kg)', tooltip: 'Total kilograms lifted per set.' },
  weight_lbs: { field: 'weightLbs', label: 'Weight (Lbs)', tooltip: 'Pounds per rep/set.' },
  weight_total_lbs: { field: 'weightTotalLbs', label: 'Weight (Total Lbs)', tooltip: 'Total pounds lifted per set.' },
};

const PRESCRIPTION_SELECT_MENU_PROPS = {
  getContentAnchorEl: null,
  anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
  transformOrigin: { vertical: 'top', horizontal: 'left' },
  PaperProps: {
    style: {
      borderRadius: 14,
      marginTop: 8,
    },
  },
};

const prescriptionMenuItemStyle = {
  color: '#002D55',
  fontSize: 'clamp(0.69rem, 0.69vw, 0.93rem)',
};

const ADAPTIVE_SOURCE_LABELS = {
  treatment_override: 'Patient-specific',
  clinician_template: 'Clinician template',
  global_default: 'Library default',
  unconfigured: 'Not configured',
  unavailable: 'Unavailable',
};

function getApiErrorMessage(response, fallbackMessage) {
  return response
    .json()
    .then((payload) => payload?.detail || payload?.error || fallbackMessage)
    .catch(() => fallbackMessage);
}

function parseYouTubeStartSeconds(rawValue) {
  if (!rawValue) {
    return 0;
  }

  const trimmed = String(rawValue).trim().toLowerCase();
  if (/^\d+$/.test(trimmed)) {
    return parseInt(trimmed, 10);
  }

  const match = trimmed.match(/(?:(\d+)h)?(?:(\d+)m)?(?:(\d+)s)?/);
  if (!match) {
    return 0;
  }

  const hours = parseInt(match[1] || '0', 10);
  const minutes = parseInt(match[2] || '0', 10);
  const seconds = parseInt(match[3] || '0', 10);
  return (hours * 3600) + (minutes * 60) + seconds;
}

function getVideoPreviewConfig(videoUrl) {
  if (!videoUrl) {
    return { kind: null, src: '' };
  }

  try {
    const parsedUrl = new URL(videoUrl);
    const host = parsedUrl.hostname.toLowerCase();
    let videoId = '';

    if (host.includes('youtu.be')) {
      videoId = parsedUrl.pathname.split('/').filter(Boolean)[0] || '';
    } else if (host.includes('youtube.com')) {
      if (parsedUrl.pathname === '/watch') {
        videoId = parsedUrl.searchParams.get('v') || '';
      } else if (parsedUrl.pathname.startsWith('/embed/')) {
        videoId = parsedUrl.pathname.split('/embed/')[1]?.split('/')[0] || '';
      } else if (parsedUrl.pathname.startsWith('/shorts/')) {
        videoId = parsedUrl.pathname.split('/shorts/')[1]?.split('/')[0] || '';
      }
    }

    if (videoId) {
      const startSeconds = parseYouTubeStartSeconds(
        parsedUrl.searchParams.get('t') || parsedUrl.searchParams.get('start')
      );
      const embedUrl = new URL(`https://www.youtube.com/embed/${videoId}`);
      if (startSeconds > 0) {
        embedUrl.searchParams.set('start', String(startSeconds));
      }
      embedUrl.searchParams.set('rel', '0');
      return { kind: 'youtube', src: embedUrl.toString() };
    }
  } catch (error) {
    // Fallback to the native video element for non-URL or direct-media values.
  }

  return { kind: 'direct', src: videoUrl };
}

function getFrontendBaseUrl() {
  const configuredBase = (process.env.REACT_APP_FRONTEND_URL || '').toString().trim();
  if (configuredBase) {
    return configuredBase.replace(/\/$/, '');
  }

  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin.replace(/\/$/, '');
  }

  return 'http://127.0.0.1:3000';
}

function normalizeExerciseMediaUploadLink(rawUrl) {
  const value = (rawUrl || '').toString().trim();
  if (!value) {
    return '';
  }

  const frontendBase = getFrontendBaseUrl();

  try {
    const parsed = new URL(value, frontendBase);
    if (parsed.pathname === '/exercise-media-upload.html' || parsed.pathname.startsWith('/exercise-media-upload/')) {
      return `${frontendBase}${parsed.pathname}${parsed.search}${parsed.hash}`;
    }
    return parsed.toString();
  } catch (error) {
    if (value.startsWith('/exercise-media-upload.html') || value.startsWith('/exercise-media-upload/')) {
      return `${frontendBase}${value}`;
    }
    return value;
  }
}

function getAdaptiveSourceLabel(source) {
  return ADAPTIVE_SOURCE_LABELS[source] || 'Adaptive';
}

function normalizeExerciseKey(value) {
  return String(value || '').trim().toLowerCase();
}

function getAdaptiveSeedBodyRegion(exercise, fallbackExercise = null) {
  const regionSources = [exercise, fallbackExercise].filter(Boolean);

  for (const source of regionSources) {
    const candidate = source?.bodyRegion ?? source?.body_region ?? source?.region ?? source?.bodyPart ?? source?.body_part ?? null;
    if (Array.isArray(candidate)) {
      const match = candidate.find((region) => typeof region === 'string' && region.trim());
      if (match) {
        return match.trim();
      }
    }
    if (typeof candidate === 'string' && candidate.trim()) {
      return candidate.trim();
    }
  }

  return '';
}

function buildAdaptivePickerInitialFilters(targetExercise, libraryExercises, reasonKey) {
  const fallbackExercise = (libraryExercises || []).find((exercise) => (
    targetExercise?.identifier && String(exercise?.identifier || '') === String(targetExercise.identifier)
  ) || (
    normalizeExerciseKey(exercise?.title) && normalizeExerciseKey(exercise?.title) === normalizeExerciseKey(targetExercise?.title)
  )) || null;
  const parsedLevel = Number(targetExercise?.exerciseLevel);
  const filterBodyRegion = getAdaptiveSeedBodyRegion(targetExercise, fallbackExercise);
  let levelDelta = 0;

  if (reasonKey === 'too_hard' || reasonKey === 'too_painful') {
    levelDelta = -1;
  } else if (reasonKey === 'too_easy') {
    levelDelta = 1;
  }

  return {
    searchInput: '',
    filterType: typeof targetExercise?.exerciseType === 'string' ? targetExercise.exerciseType.trim() : '',
    filterLevel: Number.isFinite(parsedLevel) && parsedLevel > 0
      ? String(Math.max(1, parsedLevel + levelDelta))
      : '',
    filterEquipment: '',
    filterBodyRegion,
  };
}

export default function ExerciseEditDialog({
  open,
  onClose,
  onConfirm,
  selectedExercise,
  editedParams,
  onParamChange,
  dialogTitle = "Edit Exercise",
  exerciseQueue,
  onConfirmAll,
  viewOnly = false,
  phoneUploadContext = null,
  forceMediaDrawer = false,
  libraryExercises = [],
}) {
  const classes = useStyles();
  const authToken = useSelector((state) => state.auth.token);
  const desktopUploadInputRef = React.useRef(null);

  const [queueIndex, setQueueIndex] = React.useState(0);
  const [queueParamsArray, setQueueParamsArray] = React.useState([]);
  const [mediaDrawerOpen, setMediaDrawerOpen] = React.useState(false);
  const [adaptiveDrawerOpen, setAdaptiveDrawerOpen] = React.useState(false);
  const [patientMediaPromptError, setPatientMediaPromptError] = React.useState('');
  const [phoneUploadState, setPhoneUploadState] = React.useState(null);
  const [phoneUploadLoading, setPhoneUploadLoading] = React.useState(false);
  const [phoneUploadError, setPhoneUploadError] = React.useState('');
  const [adaptiveConfig, setAdaptiveConfig] = React.useState(null);
  const [adaptiveSelections, setAdaptiveSelections] = React.useState({});
  const [adaptiveSelectionDetails, setAdaptiveSelectionDetails] = React.useState({});
  const [adaptiveRememberMap, setAdaptiveRememberMap] = React.useState({});
  const [adaptiveConfigLoading, setAdaptiveConfigLoading] = React.useState(false);
  const [adaptiveConfigError, setAdaptiveConfigError] = React.useState('');
  const [adaptiveSaveLoading, setAdaptiveSaveLoading] = React.useState(false);
  const [adaptiveSaveError, setAdaptiveSaveError] = React.useState('');
  const [adaptivePickerContext, setAdaptivePickerContext] = React.useState(null);
  const phoneUploadBaselineRef = React.useRef({ img: '', video: '' });

  React.useEffect(() => {
    if (open && exerciseQueue && exerciseQueue.length > 0) {
      setQueueIndex(0);
      setQueueParamsArray(exerciseQueue.map(ex => ({
        sets: ex.sets ?? 3,
        repetitions: ex.repetitions ?? 10,
        repetitionMaximum: ex.repetitionMaximum ?? 1,
        weightKg: ex.weightKg ?? 0,
        weightTotalKg: ex.weightTotalKg ?? 0,
        weightLbs: ex.weightLbs ?? 0,
        weightTotalLbs: ex.weightTotalLbs ?? 0,
        time: typeof ex.time === 'string' ? (parseInt(ex.time) || 30) : (ex.time ?? 30),
        pause: typeof ex.pause === 'string' ? (parseInt(ex.pause) || 15) : (ex.pause ?? 15),
        frequencyType: ex.frequencyType ?? 'daily',
        frequency: ex.frequency ?? 1,
        daysInbetween: ex.frequencyType === 'weekly'
          ? (ex.daysInbetween ?? ex.daysInBetween ?? ex.frequency ?? 1)
          : '',
        index: ex.index ?? 1,
        description: ex.description ?? '',
        title: ex.title ?? '',
        doseType: ex.doseType || ((ex.time && ex.time !== 0) ? 'time' : 'repetitions'),
      })));
    }
  }, [open, exerciseQueue]);

  const isQueueMode = !!(exerciseQueue && exerciseQueue.length > 0);
  const currentExercise = isQueueMode ? exerciseQueue[queueIndex] : selectedExercise;
  const effectiveParams = isQueueMode ? (queueParamsArray[queueIndex] || {}) : editedParams;
  const weightDoseOption = WEIGHT_DOSE_OPTIONS[effectiveParams.doseType];
  const originalImageUrl = (currentExercise?.img ?? currentExercise?.picture ?? currentExercise?.pictureURL ?? currentExercise?.image_url ?? '').toString().trim();
  const originalVideoUrl = (currentExercise?.video ?? currentExercise?.videoURL ?? currentExercise?.video_url ?? '').toString().trim();
  const imageUrl = (effectiveParams?.img ?? currentExercise?.img ?? currentExercise?.picture ?? currentExercise?.pictureURL ?? currentExercise?.image_url ?? '').toString().trim();
  const previewImageUrl = resolveExerciseImageUrl(imageUrl);
  const videoUrl = (effectiveParams?.video ?? currentExercise?.video ?? currentExercise?.videoURL ?? currentExercise?.video_url ?? '').toString().trim();
  const videoPreview = getVideoPreviewConfig(videoUrl);
  const currentExerciseId = currentExercise?.id ? String(currentExercise.id) : '';
  const adaptiveExerciseIdentifier = (currentExercise?.identifier || '').toString().trim();
  const supportsPhoneUpload = Boolean(
    open &&
    !viewOnly &&
    !isQueueMode &&
    phoneUploadContext?.patientId &&
    phoneUploadContext?.treatmentId &&
    (currentExerciseId || phoneUploadContext?.allowDraftUpload)
  );
  const supportsAdaptiveConfig = Boolean(
    open &&
    !isQueueMode &&
    !viewOnly &&
    adaptiveExerciseIdentifier &&
    phoneUploadContext?.treatmentId &&
    authToken
  );
  const availableAdaptiveLibraryExercises = React.useMemo(() => (
    Array.isArray(libraryExercises)
      ? libraryExercises.filter((exercise) => String(exercise?.identifier || '').trim() && String(exercise?.identifier || '') !== adaptiveExerciseIdentifier)
      : []
  ), [adaptiveExerciseIdentifier, libraryExercises]);
  const showsCustomMediaFields = forceMediaDrawer || !currentExercise?.identifier || currentExercise?.exerciseType === 'custom' || Boolean(imageUrl || videoUrl) || supportsPhoneUpload;
  const phoneUploadHandoffId = phoneUploadState?.id ? String(phoneUploadState.id) : '';
  const phoneUploadLink = normalizeExerciseMediaUploadLink(phoneUploadState?.mobile_url || '');
  const showPhoneUploadQrPanel = Boolean(phoneUploadLink);
  const mediaScanStatus = phoneUploadState?.status || '';
  const isMediaScanInProgress = mediaScanStatus === 'scanning';
  const isMediaScanRejected = mediaScanStatus === 'rejected' || mediaScanStatus === 'failed';
  const hasUnresolvedMediaScan = isMediaScanInProgress || isMediaScanRejected;
  const hasCustomUploadedMedia = Boolean(
    (effectiveParams?.img || '').toString().trim() && (effectiveParams?.img || '').toString().trim() !== originalImageUrl
  ) || Boolean(
    (effectiveParams?.video || '').toString().trim() && (effectiveParams?.video || '').toString().trim() !== originalVideoUrl
  );
  const requiresPatientMediaAnswer = Boolean(
    !viewOnly &&
    !isQueueMode &&
    hasCustomUploadedMedia
  );
  const shouldDefaultOpenMediaDrawer = Boolean(
    open &&
    !viewOnly &&
    !isQueueMode &&
    (
      currentExercise?.isNewCustomExercise ||
      (!currentExerciseId && (currentExercise?.exerciseType === 'custom' || !currentExercise?.identifier))
    )
  );

  const initializeAdaptiveState = React.useCallback((payload) => {
    const nextSelections = {};
    (payload?.choices || []).forEach((choice) => {
      nextSelections[choice.reason_key] = choice?.selected_exercise?.identifier || '';
    });
    setAdaptiveConfig(payload || null);
    setAdaptiveSelections(nextSelections);
    setAdaptiveRememberMap({});
    setAdaptiveConfigError('');
    setAdaptiveSaveError('');
  }, []);

  const resetAdaptiveState = React.useCallback(() => {
    setAdaptiveConfig(null);
    setAdaptiveSelections({});
    setAdaptiveRememberMap({});
    setAdaptiveConfigError('');
    setAdaptiveSaveError('');
    setAdaptiveConfigLoading(false);
    setAdaptiveSaveLoading(false);
    setAdaptivePickerContext(null);
  }, []);

  React.useEffect(() => {
    setMediaDrawerOpen(shouldDefaultOpenMediaDrawer);
    setPatientMediaPromptError('');
  }, [
    open,
    queueIndex,
    currentExerciseId,
    currentExercise?.identifier,
    currentExercise?.exerciseType,
    currentExercise?.isNewCustomExercise,
    isQueueMode,
    shouldDefaultOpenMediaDrawer,
    viewOnly,
  ]);

  React.useEffect(() => {
    if (!supportsAdaptiveConfig) {
      resetAdaptiveState();
      return undefined;
    }

    let cancelled = false;
    const params = new URLSearchParams();
    if (phoneUploadContext?.patientId) {
      params.set('patient_id', phoneUploadContext.patientId);
    }

    const loadAdaptiveConfiguration = async () => {
      setAdaptiveConfigLoading(true);
      setAdaptiveConfigError('');
      try {
        const response = await fetch(
          `/treatment/treatments/${phoneUploadContext.treatmentId}/adaptive-exercises/${encodeURIComponent(adaptiveExerciseIdentifier)}/?${params.toString()}`,
          {
            headers: {
              Authorization: `Token ${authToken}`,
            },
          }
        );

        if (!response.ok) {
          throw new Error(await getApiErrorMessage(response, 'Unable to load adaptive logic.'));
        }

        const payload = await response.json();
        if (!cancelled) {
          initializeAdaptiveState(payload);
        }
      } catch (error) {
        if (!cancelled) {
          setAdaptiveConfig(null);
          setAdaptiveSelections({});
          setAdaptiveRememberMap({});
          setAdaptiveConfigError(error.message || 'Unable to load adaptive logic.');
        }
      } finally {
        if (!cancelled) {
          setAdaptiveConfigLoading(false);
        }
      }
    };

    loadAdaptiveConfiguration();

    return () => {
      cancelled = true;
    };
  }, [adaptiveExerciseIdentifier, authToken, initializeAdaptiveState, open, phoneUploadContext?.patientId, phoneUploadContext?.treatmentId, resetAdaptiveState, supportsAdaptiveConfig]);

  React.useEffect(() => {
    if (requiresPatientMediaAnswer) {
      return;
    }

    setPatientMediaPromptError('');
  }, [requiresPatientMediaAnswer]);

  const effectiveOnParamChange = React.useCallback((field, value) => {
    if (isQueueMode) {
      setQueueParamsArray(prev => {
        const next = [...prev];
        next[queueIndex] = { ...next[queueIndex], [field]: value };
        return next;
      });
      return;
    }

    if (typeof onParamChange === 'function') {
      onParamChange(field, value);
    }
  }, [isQueueMode, queueIndex, onParamChange]);

  const handleNext = () => setQueueIndex(prev => prev + 1);
  const handlePrevious = () => setQueueIndex(prev => Math.max(0, prev - 1));

  const handleConfirmQueue = () => {
    if (onConfirmAll) {
      onConfirmAll(exerciseQueue.map((ex, i) => ({ ...ex, ...(queueParamsArray[i] || {}) })));
    }
  };

  const applyUploadedMediaToDraft = React.useCallback((payload) => {
    if (!payload?.media_url) {
      return;
    }

    if (payload.updated_field === 'picture' || payload.uploaded_media_type === 'image') {
      effectiveOnParamChange('img', payload.media_url);
    }

    if (payload.updated_field === 'video' || payload.uploaded_media_type === 'video') {
      effectiveOnParamChange('video', payload.media_url);
    }
  }, [effectiveOnParamChange]);

  const rememberPhoneUploadBaseline = React.useCallback(() => {
    phoneUploadBaselineRef.current = {
      img: effectiveParams?.img ?? currentExercise?.img ?? currentExercise?.picture ?? currentExercise?.image_url ?? '',
      video: effectiveParams?.video ?? currentExercise?.video ?? currentExercise?.video_url ?? '',
    };
  }, [effectiveParams, currentExercise]);

  const fetchPhoneUploadStatus = React.useCallback(async (handoffId, { silent = false } = {}) => {
    if (!handoffId) {
      return null;
    }

    if (!silent) {
      setPhoneUploadLoading(true);
    }

    try {
      const response = await fetch(`${backendBaseUrl}/treatment/exercise-media-handoffs/by-id/${handoffId}/`, {
        headers: authToken ? { Authorization: `Token ${authToken}` } : undefined,
      });
      if (!response.ok) {
        throw new Error(await getApiErrorMessage(response, 'Unable to refresh phone upload status.'));
      }

      const payload = await response.json();
      setPhoneUploadState(payload);
      setPhoneUploadError('');
      applyUploadedMediaToDraft(payload);
      return payload;
    } catch (error) {
      if (!silent) {
        setPhoneUploadError(error.message || 'Unable to refresh phone upload status.');
      }
      return null;
    } finally {
      if (!silent) {
        setPhoneUploadLoading(false);
      }
    }
  }, [applyUploadedMediaToDraft, authToken]);

  const handleStartPhoneUpload = React.useCallback(async () => {
    if (!supportsPhoneUpload) {
      return;
    }

    if (!authToken) {
      setPhoneUploadError('Phone upload requires a logged-in clinician session.');
      return;
    }

    setPhoneUploadLoading(true);
    setPhoneUploadError('');
    rememberPhoneUploadBaseline();

    try {
      const response = await fetch(`${backendBaseUrl}/treatment/exercise-media-handoffs/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Token ${authToken}`,
        },
        body: JSON.stringify({
          treatment_id: phoneUploadContext.treatmentId,
          patient_id: phoneUploadContext.patientId,
          exercise_id: currentExerciseId || null,
        }),
      });

      if (!response.ok) {
        throw new Error(await getApiErrorMessage(response, 'Unable to create phone upload handoff.'));
      }

      const payload = await response.json();
      setPhoneUploadState(payload);
      applyUploadedMediaToDraft(payload);
    } catch (error) {
      setPhoneUploadError(error.message || 'Unable to create phone upload handoff.');
    } finally {
      setPhoneUploadLoading(false);
    }
  }, [authToken, currentExerciseId, phoneUploadContext, supportsPhoneUpload, applyUploadedMediaToDraft]);

  const handleCancelPhoneUpload = React.useCallback(() => {
    effectiveOnParamChange('img', phoneUploadBaselineRef.current.img || '');
    effectiveOnParamChange('video', phoneUploadBaselineRef.current.video || '');
    effectiveOnParamChange(
      'patientInMedia',
      typeof currentExercise?.patientInMedia === 'boolean' ? currentExercise.patientInMedia : null
    );
    setPhoneUploadState(null);
    setPhoneUploadError('');
    setPhoneUploadLoading(false);
  }, [currentExercise, effectiveOnParamChange]);

  const handleDesktopMediaSelected = React.useCallback(async (event) => {
    const file = event.target.files && event.target.files[0];
    event.target.value = '';
    if (!file || !phoneUploadHandoffId) {
      return;
    }

    if (!authToken) {
      setPhoneUploadError('Desktop upload requires a logged-in clinician session.');
      return;
    }

    const inferredMediaType = (file.type || '').toLowerCase().startsWith('video/') ? 'video' : 'image';
    setPhoneUploadLoading(true);
    setPhoneUploadError('');

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('media_type', inferredMediaType);

      const response = await fetch(`${backendBaseUrl}/treatment/exercise-media-handoffs/by-id/${phoneUploadHandoffId}/upload/`, {
        method: 'POST',
        headers: {
          Authorization: `Token ${authToken}`,
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error(await getApiErrorMessage(response, 'Unable to upload media from this computer.'));
      }

      const payload = await response.json();
      setPhoneUploadState(payload);
      applyUploadedMediaToDraft(payload);
    } catch (error) {
      setPhoneUploadError(error.message || 'Unable to upload media from this computer.');
    } finally {
      setPhoneUploadLoading(false);
    }
  }, [applyUploadedMediaToDraft, authToken, phoneUploadHandoffId]);

  const handleAdaptiveSelectionChange = React.useCallback((reasonKey, value) => {
    setAdaptiveSelections((prev) => ({
      ...prev,
      [reasonKey]: value,
    }));
    if (!value) {
      setAdaptiveSelectionDetails((prev) => {
        const next = { ...prev };
        delete next[reasonKey];
        return next;
      });
    }
    setAdaptiveSaveError('');
  }, []);

  const handleAdaptiveRememberToggle = React.useCallback((reasonKey, checked) => {
    setAdaptiveRememberMap((prev) => ({
      ...prev,
      [reasonKey]: checked,
    }));
    setAdaptiveSaveError('');
  }, []);

  const openAdaptivePicker = React.useCallback((choice) => {
    setAdaptivePickerContext({
      reasonKey: choice.reason_key,
      reasonLabel: choice.reason_label,
      initialFilters: buildAdaptivePickerInitialFilters(currentExercise, availableAdaptiveLibraryExercises, choice.reason_key),
      initialFiltersKey: `${choice.reason_key}-${currentExercise?.identifier || currentExercise?.title || ''}-${Date.now()}`,
    });
  }, [availableAdaptiveLibraryExercises, currentExercise]);

  const closeAdaptivePicker = React.useCallback(() => {
    setAdaptivePickerContext(null);
  }, []);

  const handleAdaptiveLibraryExerciseSelect = React.useCallback((exercise) => {
    if (!adaptivePickerContext?.reasonKey || !exercise?.identifier) {
      return;
    }

    handleAdaptiveSelectionChange(adaptivePickerContext.reasonKey, exercise.identifier);
    setAdaptiveSelectionDetails((prev) => ({
      ...prev,
      [adaptivePickerContext.reasonKey]: exercise,
    }));
    setAdaptiveDrawerOpen(true);
    closeAdaptivePicker();
  }, [adaptivePickerContext, closeAdaptivePicker, handleAdaptiveSelectionChange]);

  const adaptiveDirtyCount = React.useMemo(() => {
    return (adaptiveConfig?.choices || []).reduce((count, choice) => {
      const selectedIdentifier = adaptiveSelections[choice.reason_key] ?? choice?.selected_exercise?.identifier ?? '';
      const currentIdentifier = choice?.selected_exercise?.identifier || '';
      const rememberForClinician = Boolean(adaptiveRememberMap[choice.reason_key]);
      return selectedIdentifier !== currentIdentifier || rememberForClinician ? count + 1 : count;
    }, 0);
  }, [adaptiveConfig, adaptiveRememberMap, adaptiveSelections]);

  const saveAdaptiveConfiguration = React.useCallback(async () => {
    if (!supportsAdaptiveConfig || !adaptiveConfig) {
      return true;
    }

    const changedChoices = (adaptiveConfig.choices || []).reduce((result, choice) => {
      const selectedIdentifier = (adaptiveSelections[choice.reason_key] ?? choice?.selected_exercise?.identifier ?? '').toString().trim();
      const currentIdentifier = (choice?.selected_exercise?.identifier || '').toString().trim();
      const rememberForClinician = Boolean(adaptiveRememberMap[choice.reason_key]);

      if (selectedIdentifier === currentIdentifier && !rememberForClinician) {
        return result;
      }

      result.push({
        reason_key: choice.reason_key,
        replacement_identifier: selectedIdentifier || null,
        remember_for_clinician: rememberForClinician,
      });
      return result;
    }, []);

    if (changedChoices.length === 0) {
      return true;
    }

    setAdaptiveSaveLoading(true);
    setAdaptiveSaveError('');
    try {
      const response = await fetch(
        `/treatment/treatments/${phoneUploadContext.treatmentId}/adaptive-exercises/${encodeURIComponent(adaptiveExerciseIdentifier)}/`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Token ${authToken}`,
          },
          body: JSON.stringify({
            patient_id: phoneUploadContext?.patientId || null,
            choices: changedChoices,
          }),
        }
      );

      if (!response.ok) {
        throw new Error(await getApiErrorMessage(response, 'Unable to save adaptive logic.'));
      }

      const payload = await response.json();
      initializeAdaptiveState(payload);
      return true;
    } catch (error) {
      setAdaptiveSaveError(error.message || 'Unable to save adaptive logic.');
      setAdaptiveDrawerOpen(true);
      return false;
    } finally {
      setAdaptiveSaveLoading(false);
    }
  }, [adaptiveConfig, adaptiveExerciseIdentifier, adaptiveRememberMap, adaptiveSelections, authToken, initializeAdaptiveState, phoneUploadContext, supportsAdaptiveConfig]);

  const handleConfirmAction = React.useCallback(async () => {
    if (hasUnresolvedMediaScan) {
      setPhoneUploadError(
        isMediaScanRejected
          ? 'Upload rejected. Replace the file before saving this exercise.'
          : 'Media is still being scanned. Please wait before saving this exercise.'
      );
      setMediaDrawerOpen(true);
      return;
    }

    if (requiresPatientMediaAnswer && typeof effectiveParams?.patientInMedia !== 'boolean') {
      setPatientMediaPromptError('Please confirm whether the patient appears in the uploaded image or video.');
      setMediaDrawerOpen(true);
      return;
    }

    setPatientMediaPromptError('');

    if (isQueueMode) {
      handleConfirmQueue();
      return;
    }

    const adaptiveSaved = await saveAdaptiveConfiguration();
    if (!adaptiveSaved) {
      return;
    }

    if (typeof onConfirm === 'function') {
      onConfirm();
    }
  }, [effectiveParams?.patientInMedia, handleConfirmQueue, hasUnresolvedMediaScan, isMediaScanRejected, isQueueMode, onConfirm, requiresPatientMediaAnswer, saveAdaptiveConfiguration]);

  React.useEffect(() => {
    if (!open) {
      setPhoneUploadState(null);
      setPhoneUploadError('');
      setPhoneUploadLoading(false);
      resetAdaptiveState();
      phoneUploadBaselineRef.current = { img: '', video: '' };
      return;
    }

    setPhoneUploadState(null);
    setPhoneUploadError('');
    phoneUploadBaselineRef.current = {
      img: (effectiveParams?.img ?? currentExercise?.img ?? currentExercise?.picture ?? currentExercise?.image_url ?? ''),
      video: (effectiveParams?.video ?? currentExercise?.video ?? currentExercise?.video_url ?? ''),
    };
  }, [open, currentExerciseId, phoneUploadContext?.patientId, phoneUploadContext?.treatmentId, currentExercise, resetAdaptiveState]);

  React.useEffect(() => {
    if (!open || !phoneUploadHandoffId || !['pending', 'scanning'].includes(phoneUploadState.status) || phoneUploadState.is_expired) {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      fetchPhoneUploadStatus(phoneUploadHandoffId, { silent: true });
    }, 3000);

    return () => window.clearInterval(intervalId);
  }, [open, phoneUploadHandoffId, phoneUploadState?.status, phoneUploadState?.is_expired, fetchPhoneUploadStatus]);

  React.useEffect(() => {
    if (!open || !supportsPhoneUpload || phoneUploadHandoffId || phoneUploadLoading) {
      return;
    }
    handleStartPhoneUpload();
  }, [open, supportsPhoneUpload, phoneUploadHandoffId, phoneUploadLoading, handleStartPhoneUpload]);

  if (!currentExercise) return null;

  const phoneUploadUnavailableReason = !open || viewOnly
    ? ''
    : isQueueMode
      ? 'Phone upload is only available when editing one saved patient exercise at a time.'
          : !(currentExerciseId || phoneUploadContext?.allowDraftUpload)
        ? 'Save this exercise to the patient program before using phone upload.'
        : !(phoneUploadContext?.patientId && phoneUploadContext?.treatmentId)
          ? 'Phone upload is unavailable because the patient treatment context is missing.'
          : '';

  return (
    <>
    <Dialog open={open} onClose={onClose} fullWidth PaperProps={{ style: { borderRadius: 18, maxWidth: 800 } }}>
      <DialogTitle className={classes.dialogTitle}>
        <Box className={classes.dialogTitleRow}>
          <Typography className={classes.dialogTitleText}>{dialogTitle || 'Edit Exercise'}</Typography>
          <Box className={classes.dialogTitleActions}>
            {isQueueMode && (
              <span className={classes.dialogCounter}>
                {queueIndex + 1} / {exerciseQueue.length}
              </span>
            )}
            <IconButton onClick={onClose} className={classes.closeButton}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent className={classes.dialogContent}>
          <Typography className={classes.sectionLabel}>Exercise details</Typography>
          <Box className={classes.sectionPanel}>
            <Grid container direction="column" spacing={2}>
              <Grid item>
                <TextField
                  label="Title"
                  fullWidth
                  value={effectiveParams.title ?? ''}
                  onChange={e => effectiveOnParamChange('title', e.target.value)}
                  variant="outlined"
                  size="small"
                  InputLabelProps={{ shrink: true, style: { color: '#002D55' } }}
                  InputProps={{ style: { color: '#002D55', fontWeight: 600 } }}
                  classes={{ root: classes.orangeFocused }}
                />
              </Grid>
              <Grid item>
                <Box className={classes.mediaPreview}>
                  {videoUrl ? (
                    <>
                      {videoPreview.kind === 'youtube' ? (
                        <iframe
                          title={`${currentExercise.title || 'Exercise'} video preview`}
                          src={videoPreview.src}
                          className={classes.mediaPreviewEmbed}
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                          allowFullScreen
                        />
                      ) : (
                        <video controls preload="metadata" className={classes.mediaPreviewVideo}>
                          <source src={videoPreview.src} />
                        </video>
                      )}
                    </>
                  ) : previewImageUrl ? (
                    <>
                      <ExerciseImage imageUrl={previewImageUrl} alt={currentExercise.title} className={classes.mediaPreviewImage} />
                    </>
                  ) : (
                    <>
                      <Typography className={classes.mediaPlaceholderTitle}>Add your own exercise media</Typography>
                      <Typography className={classes.mediaPlaceholderText}>
                        Use Image URL, Video URL, or scan the QR code below to record your own exercise or upload your own image or video.
                      </Typography>
                    </>
                  )}
                </Box>
              </Grid>
              <Grid item>
                <TextField
                  label="Description"
                  multiline
                  minRows={2}
                  maxRows={5}
                  fullWidth
                  value={effectiveParams.description ?? ''}
                  onChange={e => effectiveOnParamChange('description', e.target.value)}
                  variant="outlined"
                  size="small"
                  InputLabelProps={{ shrink: true, style: { color: '#002D55' } }}
                  InputProps={{
                    style: { color: '#002D55' },
                    inputProps: {
                      style: { maxHeight: 150, overflowY: 'auto', overscrollBehavior: 'contain' },
                      onWheel: event => {
                        const textArea = event.currentTarget;
                        if (textArea.scrollHeight > textArea.clientHeight) {
                          event.preventDefault();
                          event.stopPropagation();
                          textArea.scrollTop += event.deltaY;
                        }
                      },
                    },
                  }}
                  classes={{ root: classes.orangeFocused }}
                />
              </Grid>
              {showsCustomMediaFields && (
                <Grid item>
                  <ClickAwayListener onClickAway={() => setMediaDrawerOpen(false)}>
                    <Accordion
                      className={classes.mediaDrawer}
                      style={{ borderRadius: 16 }}
                      expanded={mediaDrawerOpen}
                      onChange={(_, expanded) => setMediaDrawerOpen(expanded)}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon style={{ color: '#002D55' }} />}
                        className={classes.mediaDrawerSummary}
                        aria-controls="exercise-media-adjustments-content"
                        id="exercise-media-adjustments-header"
                      >
                        <Box>
                          <Typography className={classes.mediaDrawerTitle}>Click to adjust media</Typography>
                          <Typography className={classes.mediaDrawerHint} style={{ marginBottom: 0 }}>
                            Update image and video URLs, or use the QR code record from your phone or upload your own.
                          </Typography>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails className={classes.mediaDrawerDetails}>
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={supportsPhoneUpload ? 8 : 12} md={supportsPhoneUpload ? 8 : 12}>
                            <Grid container spacing={2}>
                              <Grid item xs={12}>
                                <TextField
                                  label="Image URL"
                                  fullWidth
                                  value={effectiveParams.img ?? ''}
                                  onChange={e => effectiveOnParamChange('img', e.target.value)}
                                  variant="outlined"
                                  size="small"
                                  InputLabelProps={{ shrink: true, style: { color: '#002D55' } }}
                                  InputProps={{ style: { color: '#002D55' } }}
                                  classes={{ root: `${classes.orangeFocused} ${classes.mediaDrawerInput}` }}
                                  helperText="Paste an image URL to show a preview in the exercise card."
                                />
                              </Grid>
                              <Grid item xs={12}>
                                <TextField
                                  label="Video URL"
                                  fullWidth
                                  value={effectiveParams.video ?? ''}
                                  onChange={e => effectiveOnParamChange('video', e.target.value)}
                                  variant="outlined"
                                  size="small"
                                  InputLabelProps={{ shrink: true, style: { color: '#002D55' } }}
                                  InputProps={{ style: { color: '#002D55' } }}
                                  classes={{ root: `${classes.orangeFocused} ${classes.mediaDrawerInput}` }}
                                  helperText="Paste a video URL to show an inline preview in the exercise card."
                                />
                              </Grid>
                            </Grid>
                          </Grid>
                          {supportsPhoneUpload && (
                            <Grid item xs={12} sm={4} md={4}>
                              <Box className={classes.phoneUploadSidePanel}>
                                {showPhoneUploadQrPanel ? (
                                  <Box className={`${classes.phoneUploadQrCard} ${classes.phoneUploadQrCardCompact}`}>
                                    <input
                                      ref={desktopUploadInputRef}
                                      type="file"
                                      accept="image/*,video/*"
                                      style={{ display: 'none' }}
                                      onChange={handleDesktopMediaSelected}
                                    />
                                    <IconButton
                                      onClick={handleStartPhoneUpload}
                                      className={classes.phoneUploadQrActionLeft}
                                      disabled={phoneUploadLoading || isMediaScanInProgress}
                                      aria-label="Refresh QR code"
                                      title="Refresh QR code"
                                    >
                                      <RefreshIcon style={{ fontSize: 18 }} />
                                    </IconButton>
                                    <IconButton
                                      onClick={handleCancelPhoneUpload}
                                      className={classes.phoneUploadQrActionRight}
                                      disabled={phoneUploadLoading || isMediaScanInProgress}
                                      aria-label="Clear uploads"
                                      title="Clear uploads"
                                    >
                                      <CloseIcon style={{ fontSize: 18 }} />
                                    </IconButton>
                                    {isMediaScanInProgress && (
                                      <Tooltip title="Upload is being checked for safety. You can save the exercise after the scan finishes." arrow>
                                        <Box display="flex" alignItems="center" style={{ gap: 6, color: '#0b6aa2', marginTop: 8 }}>
                                          <CircularProgress size={16} style={{ color: '#0b6aa2' }} />
                                          <Typography className={classes.mediaPlaceholderText}>Scanning upload</Typography>
                                        </Box>
                                      </Tooltip>
                                    )}
                                    {mediaScanStatus === 'completed' && (
                                      <Tooltip title="Upload approved and attached to this exercise." arrow>
                                        <Box display="flex" alignItems="center" style={{ gap: 6, color: '#2e7d32', marginTop: 8 }}>
                                          <CheckCircleIcon style={{ fontSize: 18 }} />
                                          <Typography className={classes.mediaPlaceholderText}>Upload approved</Typography>
                                        </Box>
                                      </Tooltip>
                                    )}
                                    {isMediaScanRejected && (
                                      <Tooltip title="Upload rejected: the file was corrupt, unsafe, or the safety scan could not complete. Upload a replacement to continue." arrow>
                                        <Box display="flex" alignItems="center" style={{ gap: 6, color: '#b3261e', marginTop: 8 }}>
                                          <ErrorOutlineIcon style={{ fontSize: 18 }} />
                                          <Typography className={classes.mediaPlaceholderText}>Upload rejected</Typography>
                                        </Box>
                                      </Tooltip>
                                    )}
                                    <QRCodeSVG value={phoneUploadLink} size={112} includeMargin={false} />
                                    <Typography className={classes.mediaPlaceholderText}>
                                      Scan the QR code to use your phone, or <span
                                        role="button"
                                        tabIndex={0}
                                        className={classes.phoneUploadInlineLink}
                                        onClick={() => desktopUploadInputRef.current && desktopUploadInputRef.current.click()}
                                        onKeyDown={(event) => {
                                          if (event.key === 'Enter' || event.key === ' ') {
                                            event.preventDefault();
                                            if (desktopUploadInputRef.current) {
                                              desktopUploadInputRef.current.click();
                                            }
                                          }
                                        }}
                                      >upload</span> here.
                                    </Typography>
                                  </Box>
                                ) : phoneUploadLoading ? (
                                  <Box className={classes.phoneUploadQrPlaceholder}>
                                    <CircularProgress size={26} style={{ color: '#002D55' }} />
                                    <Typography className={classes.mediaPlaceholderTitle}>Preparing QR flow</Typography>
                                    <Typography className={classes.mediaPlaceholderText}>
                                      The scan code will appear here when the handoff is ready.
                                    </Typography>
                                    <input
                                      ref={desktopUploadInputRef}
                                      type="file"
                                      accept="image/*,video/*"
                                      style={{ display: 'none' }}
                                      onChange={handleDesktopMediaSelected}
                                    />
                                  </Box>
                                ) : (
                                  <Box className={classes.phoneUploadQrPlaceholder}>
                                    <Typography className={classes.mediaPlaceholderTitle}>QR upload</Typography>
                                    <Typography className={classes.mediaPlaceholderText}>
                                      Scan the QR code to use your phone, or upload from this computer after the handoff is ready.
                                    </Typography>
                                    <input
                                      ref={desktopUploadInputRef}
                                      type="file"
                                      accept="image/*,video/*"
                                      style={{ display: 'none' }}
                                      onChange={handleDesktopMediaSelected}
                                    />
                                  </Box>
                                )}
                              </Box>
                            </Grid>
                          )}
                        </Grid>
                        {requiresPatientMediaAnswer && (
                          <FormControl component="fieldset" className={classes.mediaConsentPanel} error={Boolean(patientMediaPromptError)}>
                            <FormLabel component="legend" className={classes.mediaConsentLabel}>
                              Does the patient appear in this uploaded image or video?
                            </FormLabel>
                            <RadioGroup
                              row
                              value={
                                effectiveParams?.patientInMedia === true
                                  ? 'yes'
                                  : effectiveParams?.patientInMedia === false
                                    ? 'no'
                                    : ''
                              }
                              onChange={(event) => {
                                setPatientMediaPromptError('');
                                effectiveOnParamChange('patientInMedia', event.target.value === 'yes');
                              }}
                            >
                              <FormControlLabel value="yes" control={<Radio color="primary" />} label="Yes" className={classes.mediaConsentOption} />
                              <FormControlLabel value="no" control={<Radio color="primary" />} label="No" className={classes.mediaConsentOption} />
                            </RadioGroup>
                            <Typography className={classes.mediaConsentHint}>
                              Ask this only for media you added or uploaded here, including QR uploads from the patient&apos;s phone.
                            </Typography>
                            {patientMediaPromptError && (
                              <Typography className={classes.mediaConsentError}>
                                {patientMediaPromptError}
                              </Typography>
                            )}
                          </FormControl>
                        )}
                      </AccordionDetails>
                    </Accordion>
                  </ClickAwayListener>
                </Grid>
              )}
              {!viewOnly && !isQueueMode && !supportsPhoneUpload && (
                <Grid item>
                  <Typography className={classes.phoneUploadHelper}>
                    {phoneUploadUnavailableReason}
                  </Typography>
                </Grid>
              )}
            </Grid>
          </Box>
          <Typography className={classes.sectionLabel}>Prescription</Typography>
          <Box className={classes.sectionPanel} style={{ marginBottom: 0 }}>
              <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      label="Sets"
                      type="number"
                      InputLabelProps={{ shrink: true, style: { color: '#002D55', minWidth: 85 } }}
                      value={effectiveParams.sets ?? 3}
                      onChange={e => effectiveOnParamChange('sets', parseInt(e.target.value, 10))}
                      variant="outlined"
                      size="small"
                      fullWidth
                      InputProps={{ style: { color: '#002D55' } }}
                      classes={{ root: classes.orangeFocused }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      select
                      label="Dose Type"
                      value={effectiveParams.doseType || 'time'}
                      onChange={e => effectiveOnParamChange('doseType', e.target.value)}
                      SelectProps={{ MenuProps: PRESCRIPTION_SELECT_MENU_PROPS }}
                      variant="outlined"
                      size="small"
                      fullWidth
                      InputLabelProps={{ shrink: true, style: { color: '#002D55' } }}
                      InputProps={{ style: { color: '#002D55' } }}
                      classes={{ root: classes.orangeFocused }}
                    >
                      <MenuItem value="time" style={prescriptionMenuItemStyle}>Time</MenuItem>
                      <MenuItem value="repetitions" style={prescriptionMenuItemStyle}>Repetitions</MenuItem>
                      <MenuItem value="repetition_maximum" style={prescriptionMenuItemStyle}>Repetition Maximum (RM)</MenuItem>
                      <MenuItem value="weight_kg" style={prescriptionMenuItemStyle}>Weight (Kg)</MenuItem>
                      <MenuItem value="weight_total_kg" style={prescriptionMenuItemStyle}>Weight (Total Kg)</MenuItem>
                      <MenuItem value="weight_lbs" style={prescriptionMenuItemStyle}>Weight (Lbs)</MenuItem>
                      <MenuItem value="weight_total_lbs" style={prescriptionMenuItemStyle}>Weight (Total Lbs)</MenuItem>
                    </TextField>
                  </Grid>
                  {effectiveParams.doseType === 'repetitions' ? (
                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        label="Rep(s)"
                        type="number"
                        InputLabelProps={{ shrink: true, style: { color: '#002D55', minWidth: 85 } }}
                        value={effectiveParams.repetitions ?? 10}
                        onChange={e => effectiveOnParamChange('repetitions', parseInt(e.target.value, 10))}
                        variant="outlined"
                        size="small"
                        fullWidth
                        InputProps={{ style: { color: '#002D55' } }}
                        classes={{ root: classes.orangeFocused }}
                      />
                    </Grid>
                  ) : effectiveParams.doseType === 'repetition_maximum' ? (
                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        label="Repetition Maximum (RM)"
                        type="number"
                        inputProps={{ min: 1 }}
                        InputLabelProps={{ shrink: true, style: { color: '#002D55', minWidth: 85 } }}
                        value={effectiveParams.repetitionMaximum ?? 1}
                        onChange={e => effectiveOnParamChange('repetitionMaximum', parseInt(e.target.value, 10))}
                        variant="outlined"
                        size="small"
                        fullWidth
                        InputProps={{ style: { color: '#002D55' } }}
                        classes={{ root: classes.orangeFocused }}
                      />
                    </Grid>
                  ) : weightDoseOption ? (
                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        label={(
                          <Tooltip title={weightDoseOption.tooltip} arrow>
                            <span>{weightDoseOption.label}</span>
                          </Tooltip>
                        )}
                        type="number"
                        inputProps={{ min: 0, step: 0.01 }}
                        InputLabelProps={{ shrink: true, style: { color: '#002D55', minWidth: 85 } }}
                        value={effectiveParams[weightDoseOption.field] ?? 0}
                        onFocus={e => {
                          if (Number(e.target.value) === 0) e.target.select();
                        }}
                        onChange={e => effectiveOnParamChange(weightDoseOption.field, e.target.value)}
                        onBlur={e => effectiveOnParamChange(
                          weightDoseOption.field,
                          e.target.value === '' ? 0 : parseFloat(e.target.value)
                        )}
                        variant="outlined"
                        size="small"
                        fullWidth
                        InputProps={{ style: { color: '#002D55' } }}
                        classes={{ root: classes.orangeFocused }}
                      />
                    </Grid>
                  ) : (
                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        label="Time (s)"
                        type="number"
                        InputLabelProps={{ shrink: true, style: { color: '#002D55' } }}
                        value={effectiveParams.time ?? 30}
                        onChange={e => effectiveOnParamChange('time', parseInt(e.target.value, 10))}
                        variant="outlined"
                        size="small"
                        fullWidth
                        InputProps={{ style: { color: '#002D55' } }}
                        classes={{ root: classes.orangeFocused }}
                      />
                    </Grid>
                  )}
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      label="Pause (s)"
                      type="number"
                      InputLabelProps={{ shrink: true, style: { color: '#002D55', minWidth: 85 } }}
                      value={effectiveParams.pause ?? 15}
                      onChange={e => effectiveOnParamChange('pause', parseInt(e.target.value, 10))}
                      variant="outlined"
                      size="small"
                      fullWidth
                      InputProps={{ style: { color: '#002D55' } }}
                      classes={{ root: classes.orangeFocused }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      select
                      label="Schedule"
                      value={effectiveParams.frequencyType ?? 'daily'}
                      onChange={e => effectiveOnParamChange('frequencyType', e.target.value)}
                      SelectProps={{ MenuProps: PRESCRIPTION_SELECT_MENU_PROPS }}
                      variant="outlined"
                      size="small"
                      fullWidth
                      InputLabelProps={{ shrink: true, style: { color: '#002D55', minWidth: 85 } }}
                      InputProps={{ style: { color: '#002D55' } }}
                      classes={{ root: classes.orangeFocused }}
                    >
                      {FrequencyType.map((option) => (
                        <MenuItem key={option.value} value={option.value} style={prescriptionMenuItemStyle}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      label="Frequency"
                      type="number"
                      InputLabelProps={{ shrink: true, style: { color: '#002D55', minWidth: 85 } }}
                      value={effectiveParams.frequency ?? 1}
                      onChange={e => effectiveOnParamChange('frequency', parseInt(e.target.value, 10))}
                      variant="outlined"
                      size="small"
                      fullWidth
                      InputProps={{ style: { color: '#002D55' } }}
                      classes={{ root: classes.orangeFocused }}
                    />
                  </Grid>
                  {(effectiveParams.frequencyType ?? 'daily') === 'weekly' && (
                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        label="Days between sessions"
                        type="number"
                        InputLabelProps={{ shrink: true, style: { color: '#002D55', minWidth: 85 } }}
                        value={effectiveParams.daysInbetween ?? ''}
                        onChange={e => effectiveOnParamChange('daysInbetween', parseInt(e.target.value, 10))}
                        variant="outlined"
                        size="small"
                        fullWidth
                        InputProps={{ style: { color: '#002D55' } }}
                        classes={{ root: classes.orangeFocused }}
                      />
                    </Grid>
                  )}
              </Grid>
          </Box>
          {supportsAdaptiveConfig && (
            <>
              <Typography className={`${classes.sectionLabel} ${classes.adaptiveSectionLabel}`}>Adaptive logic</Typography>
              <Box className={classes.sectionPanel} style={{ padding: 0, overflow: 'hidden' }}>
                <Accordion
                  className={classes.mediaDrawer}
                  style={{ borderRadius: 16, border: 'none' }}
                  expanded={adaptiveDrawerOpen}
                  onChange={(_, expanded) => setAdaptiveDrawerOpen(expanded)}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon style={{ color: '#002D55' }} />}
                    className={classes.mediaDrawerSummary}
                    aria-controls="exercise-adaptive-logic-content"
                    id="exercise-adaptive-logic-header"
                  >
                    <Box display="flex" alignItems="center" justifyContent="space-between" width="100%" style={{ gap: 12, flexWrap: 'wrap' }}>
                      <Box>
                        <Typography className={classes.mediaDrawerTitle}>Adaptive logic</Typography>
                        <Typography className={classes.mediaDrawerHint} style={{ marginBottom: 0 }}>
                          Edit patient-specific replacements for too easy, too hard, pain, and equipment issues.
                        </Typography>
                      </Box>
                      <Box display="flex" alignItems="center" style={{ gap: 8, marginLeft: 'auto' }}>
                        {adaptiveConfigLoading ? <CircularProgress size={16} style={{ color: '#002D55' }} /> : null}
                        {adaptiveConfig?.choices?.length ? (
                          <Chip
                            size="small"
                            label={`${adaptiveConfig.choices.filter((choice) => Boolean(choice.selected_exercise)).length}/${adaptiveConfig.choices.length}`}
                            className={classes.adaptiveStatusChip}
                          />
                        ) : null}
                        {adaptiveDirtyCount > 0 ? (
                          <Chip
                            size="small"
                            label={`${adaptiveDirtyCount} unsaved`}
                            className={classes.adaptiveStatusChip}
                          />
                        ) : null}
                      </Box>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails className={classes.mediaDrawerDetails}>
                    {adaptiveConfigLoading ? (
                      <Box display="flex" alignItems="center" style={{ gap: 10 }}>
                        <CircularProgress size={20} style={{ color: '#002D55' }} />
                        <Typography className={classes.phoneUploadHelper}>Loading adaptive logic...</Typography>
                      </Box>
                    ) : adaptiveConfigError ? (
                      <Typography className={classes.adaptiveChoiceError}>{adaptiveConfigError}</Typography>
                    ) : adaptiveConfig?.choices?.length ? (
                      <Box className={classes.adaptiveChoiceList} width="100%">
                        {adaptiveConfig.choices.map((choice) => {
                          const selectedIdentifier = adaptiveSelections[choice.reason_key] ?? choice?.selected_exercise?.identifier ?? '';
                          const selectedOption = adaptiveSelectionDetails[choice.reason_key]
                            || availableAdaptiveLibraryExercises.find((option) => option.identifier === selectedIdentifier)
                            || (choice.available_alternatives || []).find((option) => option.identifier === selectedIdentifier)
                            || choice.selected_exercise
                            || null;

                          return (
                            <Box key={choice.reason_key} className={classes.adaptiveChoiceCard}>
                              <Box className={classes.adaptiveChoiceHeader}>
                                <Box>
                                  <Typography className={classes.adaptiveChoiceTitle}>{choice.reason_label}</Typography>
                                </Box>
                                <Box className={classes.adaptiveChoiceHeaderActions}>
                                  <Chip
                                    size="small"
                                    label={getAdaptiveSourceLabel(choice.source)}
                                    className={classes.adaptiveStatusChip}
                                  />
                                  <Tooltip title="Reset to library default" arrow>
                                    <span>
                                      <IconButton
                                        size="small"
                                        className={classes.adaptiveChoiceHeaderButton}
                                        onClick={() => handleAdaptiveSelectionChange(choice.reason_key, '')}
                                        disabled={!selectedIdentifier}
                                      >
                                        <RefreshIcon style={{ fontSize: 14 }} />
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                </Box>
                              </Box>
                              <Box className={classes.adaptiveChoiceActionsRow}>
                                <Box className={classes.adaptiveChoiceSelectionItem}>
                                  <Box className={classes.adaptiveChoiceSelectionBox}>
                                    <Typography className={selectedOption?.title ? classes.adaptiveChoiceSelectionText : classes.adaptiveChoiceSelectionEmpty}>
                                      {selectedOption?.title || 'Using inherited adaptive choice'}
                                    </Typography>
                                  </Box>
                                </Box>
                                <Box className={classes.adaptiveChoiceButtonGroup}>
                                    <Tooltip title={`Opens the exercise library with preset search filters for ${choice.reason_label.toLowerCase()}.`} arrow>
                                      <span>
                                        <Button
                                          size="small"
                                          variant="contained"
                                          className={`${classes.confirmButton} ${classes.adaptiveChoiceActionButton}`}
                                          onClick={() => openAdaptivePicker(choice)}
                                          disabled={availableAdaptiveLibraryExercises.length === 0 && !currentExercise?.isExternal}
                                        >
                                          Replace
                                        </Button>
                                      </span>
                                    </Tooltip>
                                    {adaptiveConfig?.allows_clinician_template !== false && <FormControlLabel
                                      className={classes.adaptiveRememberOption}
                                      control={(
                                        <Checkbox
                                          color="primary"
                                          checked={Boolean(adaptiveRememberMap[choice.reason_key])}
                                          onChange={(event) => handleAdaptiveRememberToggle(choice.reason_key, event.target.checked)}
                                          disabled={!selectedIdentifier}
                                        />
                                      )}
                                      label="Remember for all my patients"
                                    />}
                                </Box>
                              </Box>
                            </Box>
                          );
                        })}
                        {adaptiveSaveError ? (
                          <Typography className={classes.adaptiveChoiceError}>{adaptiveSaveError}</Typography>
                        ) : null}
                      </Box>
                    ) : (
                      <Typography className={classes.phoneUploadHelper}>No adaptive logic is available for this exercise.</Typography>
                    )}
                  </AccordionDetails>
                </Accordion>
              </Box>
            </>
          )}
      </DialogContent>
      <DialogActions className={classes.dialogActions}>
        {viewOnly ? (
          <Button
            onClick={onClose}
            variant="outlined"
            className={classes.cancelButton}
          >
            Close
          </Button>
        ) : (
          <>
            <Button
              onClick={onClose}
              variant="outlined"
              className={classes.cancelButton}
            >
              Cancel
            </Button>
            {isQueueMode && queueIndex > 0 && (
              <Button
                onClick={handlePrevious}
                variant="outlined"
                className={classes.cancelButton}
              >
                ← Previous
              </Button>
            )}
            {isQueueMode && queueIndex < exerciseQueue.length - 1 ? (
              <Button
                onClick={handleNext}
                variant="contained"
                className={classes.confirmButton}
              >
                Next →
              </Button>
            ) : (
              <Button
                onClick={handleConfirmAction}
                variant="contained"
                className={classes.confirmButton}
                disabled={adaptiveSaveLoading || hasUnresolvedMediaScan}
              >
                {adaptiveSaveLoading ? 'Saving...' : isMediaScanInProgress ? 'Scanning media...' : isMediaScanRejected ? 'Replace rejected media' : isQueueMode ? 'Add All' : 'Confirm'}
              </Button>
            )}
          </>
        )}
      </DialogActions>
    </Dialog>
    <Drawer
      anchor="right"
      open={Boolean(adaptivePickerContext)}
      onClose={closeAdaptivePicker}
      PaperProps={{ className: classes.adaptivePickerPaper }}
    >
      <Box className={classes.adaptivePickerBody}>
        <Box className={classes.adaptivePickerHeader}>
          <Box>
            <Typography className={classes.adaptivePickerTitle}>Adaptive exercise picker</Typography>
            <Typography className={classes.adaptivePickerSubtitle}>
              {adaptivePickerContext?.reasonLabel
                ? `Preset to find exercises that fit ${adaptivePickerContext.reasonLabel.toLowerCase()}.`
                : 'Search the library and choose a replacement exercise.'}
            </Typography>
          </Box>
          <IconButton onClick={closeAdaptivePicker} className={classes.closeButton}>
            <CloseIcon />
          </IconButton>
        </Box>

        <ExerciseLibrary
          exercises={availableAdaptiveLibraryExercises}
          initialFilters={adaptivePickerContext?.initialFilters || null}
          initialFiltersKey={adaptivePickerContext?.initialFiltersKey || null}
          onExerciseSelect={handleAdaptiveLibraryExerciseSelect}
          selectionActionLabel="Use"
          title="Exercise library"
        />
      </Box>
    </Drawer>
    </>
  );
}
